/**
 * Page d'atterrissage du lien magique (/auth/magic?token=...)
 *
 * Flow :
 *   1. Lit le token depuis l'URL
 *   2. POST /api/auth/magic-link-consume → reçoit JWT + user
 *   3. Stocke la session via AuthContext.setAuthData
 *   4. Affiche un loader le temps de la requête
 *   5. Redirige vers l'espace de l'utilisateur avec ?must_set_password=1
 *      → l'app affichera la modale SetPasswordModal pour finaliser le reset
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertCircle, ShieldCheck, Sparkles, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_DASHBOARDS = {
  super_admin: '/admin/super-dashboard',
  admin: '/admin/super-dashboard',
  cooper: '/cooper',
  expert: '/expert/dashboard',
  member: '/member/dashboard',
  membre: '/member/dashboard',
};

const ROLE_LABELS = {
  super_admin: 'Super Administrateur',
  admin: 'Administrateur',
  cooper: "Conseiller COOPER'S",
  expert: 'Expert Technique & Préventif',
  member: 'Membre',
  membre: 'Membre',
};

const ROLE_ACCENT = {
  super_admin: 'from-amber-400 to-yellow-500 text-slate-900',
  admin: 'from-amber-400 to-yellow-500 text-slate-900',
  cooper: 'from-emerald-400 to-teal-500 text-white',
  expert: 'from-blue-400 to-indigo-500 text-white',
  member: 'from-violet-400 to-fuchsia-500 text-white',
  membre: 'from-violet-400 to-fuchsia-500 text-white',
};

// Cache module-level pour éviter le double consume en React Strict Mode (dev).
// useRef est réinitialisé au remount strict, contrairement à une const module.
const consumedTokens = new Map();
const inflightConsumes = new Map();

const MagicAuth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const { setAuthData } = useAuth();
  const [error, setError] = useState('');
  const [welcomeUser, setWelcomeUser] = useState(null);

  useEffect(() => {
    const consumeMagicLink = async () => {
      if (!token) {
        setError('Lien invalide : aucun token fourni dans l\'URL.');
        return;
      }
      // Si déjà consommé dans cette session JS, on récupère le résultat
      // depuis le cache au lieu de re-fetcher (sinon le backend invalide
      // le token et renvoie 400 sur le 2e appel — React Strict Mode dev).
      let data;
      if (consumedTokens.has(token)) {
        data = consumedTokens.get(token);
      } else {
        let promise = inflightConsumes.get(token);
        if (!promise) {
          promise = axios
            .post(`${BACKEND_URL}/api/auth/magic-link-consume`, { token })
            .then((res) => {
              consumedTokens.set(token, res.data);
              return res.data;
            });
          // Empêche l'unhandled rejection sur le double-fire Strict Mode
          promise.catch(() => {});
          inflightConsumes.set(token, promise);
        }
        try {
          data = await promise;
        } catch (err) {
          inflightConsumes.delete(token);
          const detail = err.response?.data?.detail;
          setError(detail || 'Lien invalide ou expiré. Veuillez refaire une demande.');
          return;
        }
      }

      const { access_token, user, must_set_password } = data;
      setAuthData(access_token, user);
      if (must_set_password) {
        sessionStorage.setItem('oscop_magic_token', token);
      }
      setWelcomeUser({ ...user, must_set_password });
      const target = ROLE_DASHBOARDS[user.role] || '/';
      const redirectUrl = must_set_password ? `${target}?must_set_password=1` : target;
      setTimeout(() => navigate(redirectUrl, { replace: true }), 1500);
    };

    consumeMagicLink();
  }, [token, setAuthData, navigate]);

  // === SPLASH "Bienvenue" après connexion magic ===
  if (welcomeUser) {
    const nom = `${welcomeUser.prenom || ''} ${welcomeUser.nom || ''}`.trim() || welcomeUser.email;
    const role = welcomeUser.role || 'member';
    const roleLabel = ROLE_LABELS[role] || 'Utilisateur';
    const accentClass = ROLE_ACCENT[role] || ROLE_ACCENT.member;
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 p-6 overflow-hidden relative"
        data-testid="magic-auth-splash"
      >
        {/* Glow décoratifs */}
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-violet-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '600ms' }} />

        <div className="relative max-w-lg w-full text-center splash-fade-in">
          {/* Logo + badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 text-xs uppercase tracking-widest font-bold">
            <Sparkles className="w-3.5 h-3.5 text-amber-300" />
            <span>O'SCOP — Connexion sécurisée</span>
          </div>

          {/* Badge rôle */}
          <div className={`inline-block mt-7 px-5 py-1.5 rounded-full bg-gradient-to-r ${accentClass} text-xs font-bold uppercase tracking-widest shadow-lg`}>
            {roleLabel}
          </div>

          {/* Nom de l'utilisateur (gros) */}
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight">
            Bienvenue
          </h1>
          <p className="mt-2 text-2xl sm:text-3xl text-yellow-300 font-semibold drop-shadow-md">
            {nom}
          </p>

          {/* Email discret */}
          <p className="mt-3 text-sm text-slate-300/80 font-mono">{welcomeUser.email}</p>

          {/* Loader vers le dashboard */}
          <div className="mt-10 flex items-center justify-center gap-2 text-slate-300">
            <span className="text-sm">Ouverture de votre espace</span>
            <ArrowRight className="w-4 h-4 splash-arrow" />
          </div>

          {/* Barre de progression */}
          <div className="mt-4 h-1 max-w-xs mx-auto rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-300 splash-progress" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950 p-6"
      data-testid="magic-auth-page"
    >
      <div className="max-w-md w-full bg-white/95 backdrop-blur rounded-2xl shadow-2xl p-8 text-center">
        {error ? (
          <div data-testid="magic-auth-error">
            <div className="w-14 h-14 mx-auto rounded-full bg-rose-100 flex items-center justify-center">
              <AlertCircle className="w-7 h-7 text-rose-600" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 mt-5">Lien invalide ou expiré</h1>
            <p className="text-sm text-slate-600 mt-3">{error}</p>
            <button
              onClick={() => navigate('/forgot-password')}
              className="mt-6 w-full h-11 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold transition-colors"
              data-testid="magic-auth-retry-btn"
            >
              Demander un nouveau lien
            </button>
          </div>
        ) : (
          <div data-testid="magic-auth-loading">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
              <ShieldCheck className="w-7 h-7 text-emerald-600" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 mt-5">Connexion sécurisée en cours…</h1>
            <p className="text-sm text-slate-600 mt-3">
              Nous validons votre lien magique et préparons votre espace personnel.
            </p>
            <div className="mt-6 flex items-center justify-center gap-2 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Veuillez patienter…</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MagicAuth;
